import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
// import { ProductOverviewAlternativeSupplierBox } from "./boxes/ProductOverviewAlternativeSupplierBox.js";
import { Modal } from "@msys/ui";
import { Grid, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { SupplierCatalogueValue } from "../../suppliers/SupplierCatalogueSelect.js";
import {
  ProductEditHeaderBox,
  FormValues as ProductEditHeaderBoxFormValues,
  useValidationSchema as useProductEditHeaderBoxFormValuesValidationSchema,
} from "../boxes/ProductEditHeaderBox.js";
import {
  ProductEditInstallationBox,
  FormValues as ProductEditInstallationBoxFormValues,
  useValidationSchema as useProductEditInstallationBoxValidationSchema,
} from "../boxes/ProductEditInstallationBox.js";
import {
  ProductEditSupplierBox,
  FormValues as ProductEditSupplierBoxFormValues,
  useValidationSchema as useProductEditSupplierBoxValidationSchema,
} from "../boxes/ProductEditSupplierBox.js";
import {
  FormValues as FilesBoxFormValues,
  ProductOverviewFilesBoxForm,
  useValidationSchema as useFilesBoxValidationSchema,
} from "../boxes/ProductOverviewFilesBoxForm.js";
import {
  ProductOverviewSupplierProductPropertyFormBox,
  FormValues as SupplierProductPropertyBoxFormValues,
  useValidationSchema as useSupplierProductPropertyBoxValidationSchema,
} from "../boxes/ProductOverviewSupplierProductPropertyBox.js";
import {
  FormValues as ThreeDBoxFormValues,
  useValidationSchema as useThreeDBoxValidationSchema,
} from "../boxes/ProductOverviewThreeDBox.js";
import { pimProductPropertyToInput } from "../helper.js";
import { ProductOverview__PimSupplierProductFragment } from "../Product.generated.js";
import {
  useSupplierCatalogueProductCreateModal__GetSupplierProductQuery,
  useSupplierCatalogueProductCreateModal__PimCreateSupplierProductMutation,
} from "./SupplierCatalogueProductCreateModal.generated.js";
import { useProductAssetUpload } from "../useProductAssetUpload.js";

type FormValues = ProductEditHeaderBoxFormValues &
  ProductEditInstallationBoxFormValues &
  ProductEditSupplierBoxFormValues &
  SupplierProductPropertyBoxFormValues &
  FilesBoxFormValues &
  ThreeDBoxFormValues;

export interface SupplierCatalogueProductCreateModalProps {
  fullHeight?: boolean;
  handleComplete: (
    product: ProductOverview__PimSupplierProductFragment,
    handleClose: () => void
  ) => Promise<void> | void;
  handleClose: () => void;
  initialSupplierCatalogue?: SupplierCatalogueValue | null;
}

export function SupplierCatalogueProductCreateModal({
  fullHeight,
  handleComplete,
  handleClose,
  initialSupplierCatalogue,
}: SupplierCatalogueProductCreateModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "Global",
    "ItemPropertyField",
    "QuoteItem",
    "Product",
  ]);

  const { getUploadUrl } = useProductAssetUpload();

  const client = useApolloClient();
  const getProductQuery =
    useSupplierCatalogueProductCreateModal__GetSupplierProductQuery({
      client,
      variables: { articleNumber: "", supplierId: "" },
      fetchPolicy: "cache-and-network",
      skip: true,
    });
  const [createSupplierCatalogueProductMutation] =
    useSupplierCatalogueProductCreateModal__PimCreateSupplierProductMutation({
      client,
    });

  const formId = React.useMemo(() => uniqueId(), []);

  const productEditHeaderBoxFormValuesValidationSchema =
    useProductEditHeaderBoxFormValuesValidationSchema();
  const productEditInstallationBoxValidationSchema =
    useProductEditInstallationBoxValidationSchema();
  const productEditSupplierBoxValidationSchema =
    useProductEditSupplierBoxValidationSchema();
  const supplierProductPropertyBoxValidationSchema =
    useSupplierProductPropertyBoxValidationSchema();
  const filesBoxValidationSchema = useFilesBoxValidationSchema();
  const threeDBoxValidationSchema = useThreeDBoxValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object()
        .shape({})
        .concat(productEditHeaderBoxFormValuesValidationSchema)
        .concat(productEditInstallationBoxValidationSchema)
        .concat(productEditSupplierBoxValidationSchema)
        .concat(supplierProductPropertyBoxValidationSchema)
        .concat(filesBoxValidationSchema)
        .concat(threeDBoxValidationSchema),
    [
      productEditHeaderBoxFormValuesValidationSchema,
      productEditInstallationBoxValidationSchema,
      productEditSupplierBoxValidationSchema,
      supplierProductPropertyBoxValidationSchema,
      filesBoxValidationSchema,
      threeDBoxValidationSchema,
    ]
  );

  const initialValues = React.useMemo(
    (): FormValues => ({
      articleNumber: "",
      supplierCatalogue: initialSupplierCatalogue ?? null,
      texts: {
        titleText: "",
        descriptionText: "",
        dimensionText: "",
        extendedDescriptionText: "",
        marketingText: "",
      },
      productType: null,
      branding: null,
      gtin: null,
      matchCode: null,
      manufacturerArticleNumber: null,
      priceUnit: "",
      priceUnitAmount: 1,
      pricing: null,
      properties: [],
      attachments: [],
      threedModel: null,
    }),
    [initialSupplierCatalogue]
  );

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        if (!(values.articleNumber && values.supplierCatalogue)) {
          throw new Error("Article number and supplier catalogue not provided");
        }

        const getProductResult = await getProductQuery.refetch({
          articleNumber: values.articleNumber,
          supplierId: values.supplierCatalogue.supplier.id,
        });

        const maybeExistingProduct = getDataOrNull(
          getProductResult.data?.pimGetSupplierProduct
        )?.supplierProduct;

        if (
          maybeExistingProduct &&
          maybeExistingProduct.status?.value !== "archived"
        ) {
          enqueueSnackbar(
            t("This product does already exist", { ns: "Product" }),
            { variant: "warning" }
          );
          return;
        }

        const attachments = await Promise.all(
          values.attachments.map(async attachment => {
            if ("file" in attachment) {
              const uploadUrl = await getUploadUrl({
                filename: attachment.file.name,
                contentType: attachment.file.type,
              });

              await fetch(uploadUrl, {
                method: "PUT",
                body: attachment.file,
              })
                .then(response => {
                  if (!response.ok) {
                    throw new Error(
                      `Failed to upload file: ${response.statusText}`
                    );
                  }
                })
                .catch(error => {
                  console.error("File upload failed", error);
                  throw error;
                });

              return {
                url: uploadUrl,
                mimeType: attachment.file.type,
                title: attachment.file.name,
              };
            } else {
              throw new Error("Only new attachments allowed");
            }
          })
        );

        const createSupplierProductResult =
          await createSupplierCatalogueProductMutation({
            variables: {
              input: {
                articleNumber: values.articleNumber,
                supplierId: values.supplierCatalogue!.supplier.id,
                supplierCatalogueId: values.supplierCatalogue!.id,
                texts: {
                  ...values.texts,
                  titleText: values.texts?.titleText ?? "",
                },
                pricing: {
                  listPrice: values.pricing?.listPrice ?? 0,
                  netPrice: values.pricing?.netPrice ?? null,
                  currency: "EUR",
                  priceUnit: values.priceUnit ?? "",
                  priceUnitAmount: values.priceUnitAmount,
                },
                branding: values.branding
                  ? {
                      brandId: values.branding.brand.id,
                      brandLineId: values.branding?.brandLine?.id,
                    }
                  : null,
                gtin: values.gtin ? values.gtin : null,
                matchCode: values.matchCode || null,
                productTypeId: values.productType?.id ?? null,
                manufacturerArticleNumber:
                  values.manufacturerArticleNumber || null,

                attachments: attachments,
                properties: values.properties.map(pimProductPropertyToInput),
                threedModelId: values.threedModel
                  ? values.threedModel.id
                  : null,
              },
            },
          });

        await handleComplete(
          createSupplierProductResult.data!.pimCreateSupplierProduct
            .supplierProduct,
          handleClose
        );
      }}
    >
      {formikProps => {
        return (
          <Modal
            title={t("Create product for supplier catalogue", {
              ns: "Product",
            })}
            handleClose={handleClose}
            dialogProps={{
              maxWidth: "md",
              PaperProps: fullHeight
                ? { style: { height: "100%" } }
                : undefined,
            }}
            actionButtons={[
              {
                label: t("Cancel", {
                  ns: "Global",
                }),
                handleClick: handleClose,
                buttonProps: { variant: "text" },
              },
              {
                label: t("Create", {
                  ns: "Global",
                }),
                buttonProps: {
                  type: "submit",
                  form: formId,
                  disabled: !formikProps.isValid || !formikProps.dirty,
                  loading: formikProps.isSubmitting,
                },
              },
            ]}
            alwaysVisible
          >
            <Form id={formId}>
              <Grid container spacing={1}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Stack direction="column" spacing={1}>
                    <ProductEditHeaderBox />
                    <ProductOverviewSupplierProductPropertyFormBox
                      itemCount={0}
                      productTypeId={formikProps.values.productType?.id ?? null}
                    />
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Stack direction="column" spacing={1}>
                    <ProductEditInstallationBox />
                    <ProductEditSupplierBox />
                    <ProductOverviewFilesBoxForm />
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}
